<template>
  <InfuseTable
    :active="(row, id) => active(row, id)"
    :columns="tableColumns"
    :has-more-items="hasMoreItems"
    :loading="loading"
    :search="search"
    :filters="filters"
    :filter-configs="filterConfigs"
    :rows="rows"
    :removable="removable"
    :remove-permission-id="commandPermissions.REMOVE_CHARGING_STATION_FROM_LOCATION"
    :selectable="selectionEnabled"
    :show-selection-chips="showSelectionChips"
    :selection-chip-label="(row) => row.id"
    :selection-chip-icon="selectionChipIcon"
    :pre-selected="preSelected"
    :total-matching="totalMatching"
    :enable-select-all-matching="enableSelectAllMatching"
    :selection-state="selectionState"
    :matching-selected="matchingSelected"
    :excluded-row-keys="excludedRowKeys"
    :selection-message="selectionMessage"
    @click-row="(row) => emit('clickRow', row)"
    @load="emit('load')"
    @add:filter="(f: Filter) => emit('add:filter', f)"
    @remove="(row) => emit('remove', row)"
    @remove:filter="(f: Filter) => emit('remove:filter', f)"
    @search="(query: string) => emit('search', query)"
    @selected="onSelected"
    @select-all-matching="emit('select-all-matching')"
    @toggle-matching-row="(row) => emit('toggle-matching-row', row)"
    @select-matching-on-screen="(visibleRows) => emit('select-matching-on-screen', visibleRows)"
    @sort="(sort) => emit('sort', sort)"
  >
    <template #actions>
      <slot name="actions" />
    </template>

    <template #mobileCard="{ row, selectable, checked, toggle }">
      <div class="flex min-w-0 flex-1 items-stretch gap-3">
        <div class="w-1 shrink-0 rounded-r-full" :class="stripClass(row)" role="presentation" />

        <div v-if="selectable" class="flex items-center" @click.stop="">
          <InfuseCheckBox :model-value="checked" @update:model-value="toggle" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-2 py-3">
          <div class="flex min-w-0 items-baseline gap-2">
            <span class="truncate text-sm font-bold text-neutral-800">{{ row.customIdentifier || row.id }}</span>
            <span v-if="row.customIdentifier" class="truncate font-mono text-xs text-neutral-500">{{ row.id }}</span>
          </div>

          <div class="flex flex-wrap items-center gap-1.5">
            <ChargingStationTag v-if="row.friendlyStatus" :status="row.friendlyStatus" size="md" />

            <InfuseTag :inline="true" size="md" variant="neutral">
              <font-awesome-icon :icon="['fal', 'bolt']" class="mr-1.5" />
              {{ evseUsage(row) }}
            </InfuseTag>

            <InfuseTag v-if="offlineDuration(row)" :inline="true" size="md" variant="warning">
              <font-awesome-icon :icon="['fal', 'bolt-slash']" class="mr-1.5" />
              {{ offlineDuration(row) }}
            </InfuseTag>

            <InfuseTag v-if="row.accepted && !row.configured" :inline="true" size="md" variant="warning">
              {{ t('chargingStation.mobileCard.configRequired') }}
            </InfuseTag>

            <InfuseTag v-if="row.blocked" :inline="true" size="md" variant="error">
              <font-awesome-icon :icon="['fal', 'ban']" class="mr-1.5" />
              {{ t('chargingStation.table.header.blocked') }}
            </InfuseTag>
          </div>
        </div>
      </div>
    </template>

    <template #deviceId="{ data, row }">
      <div class="flex flex-row gap-2">
        <div class="flex flex-col gap-2 justify-center">
          <b>{{ data }}</b>
          <div class="font-normal" v-if="row.customIdentifier">{{ row.customIdentifier }}</div>
        </div>
      </div>
    </template>

    <template #selectionActions="{ selectedRows }">
      <slot name="selectionActions" :selected-rows="selectedRows" />
    </template>

    <template #serialNumber="{ data, active }">
      <span :class="active ? 'text-primary-500' : 'text-neutral-500'">{{ data }}</span>
    </template>

    <template #pools="{ data, active }">
      <InfuseTooltip v-if="data?.length" content="locations" custom-content>
        <template #content>
          <div class="flex flex-col gap-0.5 text-xs">
            <span v-for="pool in data" :key="pool.id">{{ pool.name }}</span>
          </div>
        </template>
        <div class="max-w-[160px]">
          <span class="block truncate" :class="active ? 'text-primary-500' : 'text-neutral-500'">
            {{ formatPoolNames(data) }}
          </span>
        </div>
      </InfuseTooltip>
      <span v-else></span>
    </template>

    <template #status="{ data, row }">
      <div class="flex items-center gap-1.5">
        <ChargingStationTag :status="data" />
        <template v-if="showStatusIcons">
          <InfuseTooltip v-if="!row.accepted" :content="t('chargingStation.statusIcon.notAccepted')">
            <font-awesome-icon :icon="['fal', 'circle-check']" class="h-4 w-4 text-error-500" />
          </InfuseTooltip>
          <InfuseTooltip v-if="!row.configured" :content="t('chargingStation.statusIcon.notConfigured')">
            <font-awesome-layers class="text-error-500" style="width:1rem;height:1rem;font-size:1rem;vertical-align:-0.125em">
              <font-awesome-icon :icon="['fal', 'circle']" />
              <font-awesome-icon :icon="['fas', 'cog']" transform="shrink-7" />
            </font-awesome-layers>
          </InfuseTooltip>
          <InfuseTooltip v-if="row.blocked" :content="t('chargingStation.statusIcon.blocked')">
            <font-awesome-icon :icon="['fal', 'ban']" class="h-4 w-4 text-error-500" />
          </InfuseTooltip>
        </template>
      </div>
    </template>

    <template #evses="{ row }">
      <StatusDots :charging-station="row" />
    </template>

    <template #type="{ data, active }">
      <div v-if="data && data.model && (showChargingStationVendor ? data.vendor : true)">
        <div class="flex flex-col gap-1 justify-center">
          <span
            v-if="showChargingStationVendor"
            class="float-left w-fit"
            :class="active ? 'text-primary-500' : 'text-neutral-500'"
          >
            {{ data.vendor }}
          </span>
          <span class="font-semibold" :class="active ? 'text-primary-500' : 'text-neutral-500'">{{
            displayChargingStationType(data)
          }}</span>
        </div>
      </div>
      <div v-else>&nbsp;</div>
      <!-- show a space when no data is available -->
    </template>

    <template #firmware="{ data, active }">
      <InfuseTag v-if="data" :variant="active ? 'success' : 'neutral'">{{ data }}</InfuseTag>
    </template>

    <template #firstSeen="{ data, active }">
      <span :class="active ? 'text-primary-500' : 'text-neutral-500'">{{ formatDate(data) }}</span>
    </template>

    <template #lastSeen="{ data, active }">
      <span :class="active ? 'text-primary-500' : 'text-neutral-500'">{{ formatDate(data) }}</span>
    </template>

    <template #accepted="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>

    <template #configured="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>

    <template #blocked="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-error-500' : 'text-success-500'"
      />
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { commandPermissions } from '@infuse/api/cpms/operator';
import {
  type ChargingStation,
  type ChargingStationPool,
  ComponentStatus,
  displayChargingStationType,
  EvseStatus,
  FriendlyStatus,
  FriendlyStatusColors
} from '@infuse/cpms/charging-station/store';
import dayjs from 'dayjs';
import { InfuseCheckBox, InfuseTable, InfuseTag, InfuseTooltip } from '@infuse/ui/components';

import {
  type ColumnSort,
  type Filter,
  type FilterConfiguration,
  type UiTableColumn,
  UiTableColumnAlignment
} from '@infuse/ui/domain';
import { formatDate } from '@infuse/ui/utils';
import { useAppSettingsStore } from '@infuse/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ChargingStationTag from './ChargingStationTag.vue';
import StatusDots from './StatusDots.vue';

const { t } = useI18n();
const enableFirmwareUpdate = useAppSettingsStore().getAsBool('VITE_ENABLE_FIRMWARE_UPDATE_PREVIEW');

function formatPoolNames(pools: ChargingStationPool[]): string {
  return pools.map((p) => p.name).join(', ');
}

// Mobile summary card helpers.
const stripClasses: Record<string, string> = {
  eVcharging: 'bg-eVcharging-500',
  eVavailable: 'bg-eVavailable-500',
  eVerror: 'bg-eVerror-500',
  eVinactive: 'bg-eVinactive-500',
  eVfullyCharged: 'bg-eVfullyCharged-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  success: 'bg-success-500',
  neutral: 'bg-neutral-300'
};

function stripClass(row: ChargingStation): string {
  if (row.blocked) {
    return 'bg-error-500';
  }

  const variant = row.friendlyStatus ? FriendlyStatusColors[row.friendlyStatus] : undefined;
  return (variant && stripClasses[variant]) || 'bg-neutral-300';
}

function evseUsage(row: ChargingStation): string {
  const activeEvses = row.evses?.filter((evse) => evse.evseStatus !== EvseStatus.REMOVED) ?? [];
  const inUse = activeEvses.filter((evse) =>
    evse.connectors?.some((connector) => connector.status === ComponentStatus.OCCUPIED)
  ).length;

  return inUse > 0
    ? t('chargingStation.mobileCard.inUse', { inUse, total: activeEvses.length })
    : t('chargingStation.mobileCard.evseCount', { count: activeEvses.length });
}

function offlineDuration(row: ChargingStation): string | undefined {
  if (row.friendlyStatus !== FriendlyStatus.OFFLINE || !row.lastSeen) {
    return undefined;
  }

  const minutes = dayjs().diff(dayjs(row.lastSeen), 'minute');
  if (minutes >= 60 * 24) {
    return `${Math.floor(minutes / (60 * 24))} d`;
  }

  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)} h`;
  }

  return `${Math.max(minutes, 1)} m`;
}

interface Emits {
  (e: 'add:filter', filter: Filter): void;
  (e: 'batch'): void;
  (e: 'clickRow', row: ChargingStation): void;
  (e: 'load'): void;
  (e: 'remove', row: ChargingStation): void;
  (e: 'remove:filter', filter: Filter): void;
  (e: 'search', query: string): void;
  (e: 'selected', stations: ChargingStation[]): void;
  (e: 'select-all-matching'): void;
  (e: 'toggle-matching-row', row: ChargingStation): void;
  (e: 'select-matching-on-screen', visibleRows: ChargingStation[]): void;
  (e: 'sort', sort: ColumnSort): void;
}

const emit = defineEmits<Emits>();

interface Props {
  active?: (row: ChargingStation, id?: number) => boolean;
  columns?: UiTableColumn[];
  enableSelection?: boolean;
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
  hasMoreItems: boolean;
  hidePoolColumn?: boolean;
  showStatusIcons?: boolean;
  showSelectionChips?: boolean;
  selectionChipIcon?: string[];
  loading: boolean;
  page: number;
  removable?: boolean;
  rows: ChargingStation[];
  searchEnabled: boolean;
  searchQuery?: string;
  showChargingStationVendor?: boolean;
  totalMatching?: number;
  enableSelectAllMatching?: boolean;
  preSelected?: ChargingStation[];
  /** Selection-banner styling/message, driven by the wrapper (forwarded to InfuseTable).
   *  Declared explicitly rather than relying on attribute fall-through. */
  selectionState?: 'normal' | 'warn' | 'block';
  matchingSelected?: boolean;
  excludedRowKeys?: string[];
  selectionMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  active: () => false,
  columns: undefined,
  enableSelection: undefined,
  hasMoreItems: true,
  hidePoolColumn: false,
  showStatusIcons: false,
  showSelectionChips: false,
  loading: false,
  removable: false,
  rows: () => [],
  searchEnabled: true,
  searchQuery: '',
  // Showing the vendor next to the model is a product variant (some products show model only).
  // Default from the variant rather than `true`, so consumers that don't pass the prop
  // still render the product's intended column. Inlined because `withDefaults` is
  // hoisted out of `setup()` and cannot reference a local constant.
  showChargingStationVendor: import.meta.env.VITE_UI_VARIANT_SHOW_CHARGING_STATION_VENDOR !== 'false',
  totalMatching: undefined,
  enableSelectAllMatching: false,
  preSelected: () => [],
  selectionState: 'normal',
  matchingSelected: false,
  excludedRowKeys: () => [],
  selectionMessage: undefined
});

const selectionEnabled = computed(() => props.enableSelection ?? enableFirmwareUpdate);

function onSelected(rows: ChargingStation[]) {
  emit('selected', rows);
}

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('chargingStation.search.placeholder'),
        query: props.searchQuery
      }
);

const allColumns = computed<UiTableColumn[]>(() => [
  {
    name: 'deviceId',
    label: () => t('chargingStation.table.header.deviceId'),
    field: 'id',
    bold: true
  },
  {
    name: 'status',
    label: () => t('chargingStation.table.header.status'),
    field: 'friendlyStatus'
  },
  {
    name: 'evses',
    label: () => t('chargingStation.table.header.evses'),
    field: 'evses'
  },
  {
    name: 'type',
    label: () =>
      props.showChargingStationVendor
        ? t('chargingStation.table.header.model')
        : t('chargingStation.table.header.modelOnly'),
    field: 'type',
    hideOnMobile: true
  },
  {
    name: 'pools',
    label: () => t('chargingStation.table.header.pool'),
    field: 'pools'
  },
  {
    name: 'firmware',
    label: () => t('chargingStation.table.header.firmware'),
    field: 'firmwareVersion',
    hideOnMobile: true
  },
  {
    name: 'accepted',
    label: () => t('chargingStation.table.header.accepted'),
    field: 'accepted',
    align: UiTableColumnAlignment.CENTER,
    hideOnMobile: true
  },
  {
    name: 'configured',
    label: () => t('chargingStation.table.header.configured'),
    field: 'configured',
    align: UiTableColumnAlignment.CENTER,
    hideOnMobile: true
  },
  {
    name: 'blocked',
    label: () => t('chargingStation.table.header.blocked'),
    field: 'blocked',
    align: UiTableColumnAlignment.CENTER,
    hideOnMobile: true
  },
  {
    name: 'firstSeen',
    label: () => t('chargingStation.table.header.firstSeen'),
    field: 'firstSeen',
    sortable: true,
    hideOnMobile: true
  },
  {
    name: 'lastSeen',
    label: () => t('chargingStation.table.header.lastSeen'),
    field: 'lastSeen',
    sortable: true
  }
]);

const tableColumns = computed<UiTableColumn[]>(() => {
  if (props.columns) return props.columns;
  return props.hidePoolColumn ? allColumns.value.filter((c) => c.name !== 'pools') : allColumns.value;
});
</script>

<i18n>
{
  "en": {
    "chargingStation": {
      "mobileCard": {
        "evseCount": "{count} EVSE",
        "inUse": "{inUse} / {total} in use",
        "configRequired": "Config required"
      }
    }
  },
  "nl": {
    "chargingStation": {
      "mobileCard": {
        "evseCount": "{count} EVSE",
        "inUse": "{inUse} / {total} in gebruik",
        "configRequired": "Configuratie vereist"
      }
    }
  }
}
</i18n>
